import { Localizable } from '@components/localizable/Localizable'
import { App, Form, Modal, Space, Steps } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import { useEffect, useRef, useState } from 'react'
import { DEFAULT_ERROR } from '../../../App'
import { langList } from '../../../factories/Utils'
import { Notification } from '../../../models/Notification'
import {
  createNotification,
  updateNotification,
} from '../../../services/NotificationService'
import { Action } from './steps/Action'
import { Audience } from './steps/Audience'
import { Content } from './steps/Content'
import { NotificationName } from './steps/NotificationName'

/**
 * What `SBAPI` rejects with on a validation failure: Laravel's `errors`, bare
 * or wrapped in `data` by the toolkit, keyed `en.body`, `fr.title`…
 */
interface SaveRejection {
  error?: {
    response?: {
      data?: {
        errors?: Record<string, string[]>
        data?: { errors?: Record<string, string[]> }
      }
    }
  }
}

const validationErrors = (rejection: unknown) => {
  const data = (rejection as SaveRejection)?.error?.response?.data

  return data?.errors ?? data?.data?.errors
}

// The wizard step holding a field, so a failed save lands on it.
const stepOf = (name: NamePath): number => {
  const [root] = Array.isArray(name) ? name : [name]

  switch (root) {
    case 'name':
      return 0
    case 'audience':
    case 'loyaltyTier':
      return 1
    case 'action':
      return 2
    default:
      return 3
  }
}

interface Props {
  onClose?: () => void
  onReload?: () => void

  initialStep?: number
  selectedNotification?: Notification
  viewOnly: boolean
  open?: boolean
}

export const PushWizardModal = ({
  onClose,
  onReload,
  initialStep = undefined,
  selectedNotification,
  viewOnly,
  open,
}: Props) => {
  const [form] = Form.useForm()
  const [current, setCurrent] = useState<number>(initialStep ?? 0)
  const { message, modal } = App.useApp()
  // The wizard now stays open during the request: a second click must not
  // create the notification twice.
  const isSaving = useRef(false)

  useEffect(() => {
    if (!selectedNotification) {
      return
    }
    form.setFieldsValue(selectedNotification)
  }, [selectedNotification])

  const next = () => {
    setCurrent(current + 1)
  }

  const prev = () => {
    setCurrent(current - 1)
  }

  const close = () => {
    form.resetFields()
    setCurrent(0)
    onClose?.()
  }

  const showFailedStep = () => {
    const failed = form.getFieldsError().find(({ errors }) => errors.length)
    if (failed) {
      setCurrent(stepOf(failed.name))
    }
  }

  /**
   * Saves the notification and closes the wizard only once the API accepted
   * it. On a refusal the wizard stays open with the admin's input, and the
   * API's validation errors are pinned to their field and language.
   */
  const save = async (): Promise<boolean> => {
    if (isSaving.current) {
      return false
    }

    let values: Notification
    try {
      values = await form.validateFields()
    } catch {
      showFailedStep()
      message.error('Some fields are invalid.')
      return false
    }

    isSaving.current = true
    try {
      await (selectedNotification
        ? updateNotification(values)
        : createNotification(values))
    } catch (rejection) {
      const errors = validationErrors(rejection)
      if (errors) {
        form.setFields(
          Object.entries(errors).map(([key, messages]) => ({
            name: key.split('.'),
            errors: messages,
          }))
        )
        showFailedStep()
        message.error('The notification was not saved: check the fields.')
      } else {
        message.error(DEFAULT_ERROR)
      }
      return false
    } finally {
      isSaving.current = false
      onReload?.()
    }

    close()
    return true
  }

  // Nothing typed yet on a new notification: there is nothing to save.
  const isBlank = () =>
    !selectedNotification &&
    !form.getFieldValue('name') &&
    langList.every((lang) =>
      Object.values(form.getFieldValue(lang.code) ?? {}).every(
        (value) => !value
      )
    )

  // The cross saves like Finish does, but lets the admin walk away from a
  // notification that cannot be saved instead of trapping them in the wizard.
  const handleCancel = async () => {
    if (isSaving.current) {
      return
    }

    if (viewOnly || isBlank()) {
      close()
      return
    }

    if (await save()) {
      return
    }

    modal.confirm({
      title: 'Close without saving?',
      content:
        'The notification could not be saved. Your changes will be lost.',
      okText: 'Close without saving',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: close,
    })
  }

  const steps = [
    {
      title: 'Name',
      content: <NotificationName onNext={next} />,
    },
    {
      title: 'Audience',
      content: <Audience onNext={next} onPrev={prev} viewOnly={viewOnly} />,
    },
    {
      title: 'Actions',
      content: <Action onNext={next} onPrev={prev} viewOnly={viewOnly} />,
    },
    {
      title: 'Content',
      content: (
        <Content onPrev={prev} onFinish={viewOnly ? close : () => save()} />
      ),
    },
  ]

  return (
    <Localizable headerSelector>
      <Modal
        destroyOnHidden={true}
        open={open}
        footer={null}
        closable
        closeIcon={null}
        onCancel={handleCancel}
        width={current == 3 ? '70%' : '40%'}
      >
        <Steps
          current={current}
          items={steps.map((step) => ({ key: step, title: step.title }))}
        />
        <Form form={form} layout="vertical" disabled={viewOnly}>
          {steps.map((s, index) => (
            <Space
              key={index}
              style={{ display: current === index ? 'block' : 'none' }}
            >
              {s.content}
            </Space>
          ))}
        </Form>
      </Modal>
    </Localizable>
  )
}
